<template>
  <d-container fluid class="main-content-container px-4">
    <!-- Page Header -->
    <page-header
      title="Kategorie produktów"
      subtitle="Panel administratora"
      buttonLabel="Dodaj kategorię produktów"
      buttonLink="/admin/product-types/new"
      secondButtonLabel="Dodaj podkategorię produktów"
      secondButtonLink="/admin/product-types/new?subcategory=true"
      backButtonLabel="Wróć"
      backButtonLink="/admin/" />

    <d-row
      style="width: 100%;"
      class="d-flex ml-0 mb-3 page-filters">
      <form
        style="width: 100%;"
        class="d-flex">
        <d-input
          placeholder="Podaj nazwę kategorii produktów"
          v-model="filters.search" />

        <d-button
          theme="success"
          outline
          class="text-nowrap ml-2"
          @click.prevent="searchWithFilters">Szukaj</d-button>

        <d-button
          theme="danger"
          outline
          class="text-nowrap ml-2"
          @click.prevent="clearFilters">Resetuj filtry</d-button>
      </form>
    </d-row>

    <d-card style="width: 100%;">
      <table class="table mt-0 mb-0 full-width">
        <thead class="thead-dark">
          <tr>
            <sortable-table-heading field="name" view="product-types" :column="orderColumn" :direction="orderDirection">Typ</sortable-table-heading>
            <th scope="col" class="border-0">Edytor</th>
            <th scope="col" class="border-0">Akcje</th>
          </tr>
        </thead>
        <tbody v-if="loaded">
          <template v-for="(productType, index) of productTypes" :key="'product-type-' + index">
            <!-- Parent Category Row -->
            <tr
              :class="{'has-children': productType.children && productType.children.length}">
              <td class="align-middle">
                <router-link :to="'/admin/product-types/' + productType.id">
                  {{ productType.name }}
                </router-link>
              </td>
              <td class="align-middle" style="width: 250px;">
                <small v-if="productType.use_drink_editor" class="text-secondary"><strong>Korzysta z edytora napojów</strong></small>
                <small v-if="!productType.use_drink_editor">Korzysta z klasycznego edytora</small>
              </td>
              <td
                class="align-middle"
                style="width: 150px;">
                <d-button
                  theme="dark"
                  @click.native="$router.push('/admin/product-types/' + productType.id)"
                  class="mr-2"
                  title="Edytuj">
                  <i class="material-icons">edit</i>
                </d-button>
                <d-button
                  theme="danger"
                  :disabled="removeInProgress"
                  @click.native="removeProductType(productType.id)"
                  title="Usuń">
                  <i class="material-icons">delete</i>
                </d-button>
              </td>
            </tr>

            <!-- Children Category Rows -->
            <tr
              v-for="(child, childIndex) of productType.children"
              :key="'child-' + childIndex"
              class="subcategory">
              <td class="align-middle">
                <router-link :to="'/admin/product-types/' + child.id + '?subcategory=true'">
                  {{ child.name }}
                </router-link>
              </td>
              <td class="align-middle" style="width: 250px;">
                <small v-if="child.use_drink_editor" class="text-secondary"><strong>Korzysta z edytora napojów</strong></small>
                <small v-if="!child.use_drink_editor">Korzysta z klasycznego edytora</small>
              </td>
              <td
                class="align-middle"
                style="width: 150px;">
                <d-button
                  theme="dark"
                  @click.native="$router.push('/admin/product-types/' + child.id + '?subcategory=true')"
                  class="mr-2"
                  title="Edytuj">
                  <i class="material-icons">edit</i>
                </d-button>
                <d-button
                  theme="danger"
                  :disabled="removeInProgress"
                  @click.native="removeProductType(child.id)"
                  title="Usuń">
                  <i class="material-icons">delete</i>
                </d-button>
              </td>
            </tr>
          </template>

          <tr v-if="productTypes.length === 0">
            <td
              colspan="5"
              class="text-center">
              Brak typów produktów do wyświetlenia&hellip;
            </td>
          </tr>
        </tbody>
      </table>

      <div v-if="!loaded" class="pb-3 pt-3 text-center">
        Trwa ładowanie danych&hellip;
      </div>

      <d-alert
        v-if="loadError"
        show
        theme="warning">
        Wczytywanie danych nie powiodło się. <a href="javascript:window.location.reload();" class="alert-link">Odśwież stronę</a> aby spróbować ponownie.
      </d-alert>
    </d-card>

    <pagination :meta-data="paginationMetaData" />
  </d-container>
</template>

<script>
import debounce from 'lodash.debounce';
import HasFilters from './../mixins/HasFilters.vue';
import HasPagination from './../mixins/HasPagination.vue';
import ListUtils from './../utils/ListUtils.js';
import Pagination from './../components/common/Pagination.vue';

export default {
  name: 'product-types',
  mixins: [
    HasFilters,
    HasPagination
  ],
  components: {
    'pagination': Pagination
  },
  data() {
    return {
      productTypes: [],
      loaded: false,
      loadError: false,
      removeInProgress: false,
      filters: {
        search: ''
      }
    };
  },
  mounted () {
    this.loadFilteredData();
  },
  methods: {
    debouncedLoadFilteredData: debounce(function () {
      this.$bus.emit('view-filters-save');
      this.loadFilteredData();
    }, 250),
    loadFilteredData () {
      let where = {};

      if (this.filters.search) {
        where.search = this.filters.search;
      }

      ListUtils.loadItemsData(this, {
        endpoint: '/products/types/items?page=' + this.currentPage,
        listField: 'productTypes',
        pagination: true,
        params: {
          where,
          order: {
            column: this.orderColumn,
            direction: this.orderDirection
          }
        }
      });
    },
    removeProductType (productTypeID) {
      this.removeInProgress = true;

      // First confirmation dialog
      this.$swal({
        title: 'Czy na pewno chcesz usunąć tę kategorię produktu?',
        text: 'Tej operacji nie można cofnąć',
        showCancelButton: true,
        confirmButtonText: 'Tak, usuń',
        cancelButtonText: 'Anuluj',
        buttonsStyling: true
      }).then((result) => {
        if (result.value) {
          // Attempt deletion without force_delete first
          this.$http.post('/products/types/delete/' + productTypeID)
            .then(response => {
              if (response.data.success) {
                // Success - category was deleted
                this.$swal({
                  title: 'Usunięto kategorię produktu',
                  html: 'Wybrana kategoria produktu została usunięta',
                  icon: 'success',
                  confirmButtonText: 'OK',
                  buttonsStyling: true
                }).then(() => {
                  this.removeInProgress = false;
                  this.loadFilteredData();
                }).catch(() => {
                  this.removeInProgress = false;
                });
              } else {
                // This shouldn't happen with success: false in success response
                this.removeInProgress = false;
              }
            })
            .catch(error => {
              // Check if error is due to subcategories
              if (error.response && error.response.status === 422 && 
                  error.response.data && error.response.data.children_count > 0) {
                
                const childrenCount = error.response.data.children_count;
                
                // Show confirmation for force delete
                this.$swal({
                  title: 'Ta kategoria zawiera podkategorie',
                  html: `Ta kategoria zawiera ${childrenCount} podkategorię/podkategorii. Czy na pewno chcesz usunąć kategorię wraz ze wszystkimi jej podkategoriami?<br><br><small>Produkty zostaną zachowane jako nieprzypisane.</small>`,
                  showCancelButton: true,
                  confirmButtonText: 'Tak, usuń',
                  cancelButtonText: 'Anuluj',
                  buttonsStyling: true,
                  icon: 'warning'
                }).then((forceDeleteResult) => {
                  if (forceDeleteResult.value) {
                    // User confirmed - retry with force_delete
                    this.$http.post('/products/types/delete/' + productTypeID, { force_delete: true })
                      .then(deleteResponse => {
                        if (deleteResponse.data.success) {
                          this.$swal({
                            title: 'Usunięto kategorię produktu',
                            html: 'Kategoria i wszystkie jej podkategorie zostały usunięte. Produkty zostały zachowane jako nieprzypisane.',
                            icon: 'success',
                            confirmButtonText: 'OK',
                            buttonsStyling: true
                          }).then(() => {
                            this.removeInProgress = false;
                            this.loadFilteredData();
                          }).catch(() => {
                            this.removeInProgress = false;
                          });
                        }
                      })
                      .catch(forceDeleteError => {
                        console.log(forceDeleteError);
                        this.$swal({
                          title: 'Wystąpił błąd',
                          html: 'Usuwanie kategorii produktu nie powiodło się. Spróbuj ponownie.',
                          icon: 'error',
                          confirmButtonText: 'OK',
                          buttonsStyling: true
                        });
                        this.removeInProgress = false;
                      });
                  } else {
                    this.removeInProgress = false;
                  }
                });
              } else {
                // Other error
                console.log(error);
                this.$swal({
                  title: 'Wystąpił błąd',
                  html: 'Usuwanie kategorii produktu nie powiodło się. Spróbuj ponownie.',
                  icon: 'error',
                  confirmButtonText: 'OK',
                  buttonsStyling: true
                });
                this.removeInProgress = false;
              }
            });
        } else {
          this.removeInProgress = false;
        }
      });
    },
    clearFilters () {
      this.loaded = false;
      this.loadError = false;
      this.filters = {
        search: ''
      };

      this.loadFilteredData();
      this.$bus.emit('view-filters-reset');
    },
    searchWithFilters () {
      this.$bus.emit('pagination-reset');
      this.debouncedLoadFilteredData();
    }
  }
};
</script>

<style scoped>
.subcategory td:first-child {
  padding-left: 40px;
  position: relative;
}

.subcategory td:first-child::before {
  content: '└─';
  position: absolute;
  left: 20px;
  color: #999;
}

.has-children td {
  font-weight: 600;
}
</style>

